/** @format */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import {
  Badge,
  Button,
  Pagination,
  Select,
  Skeleton,
  TextInput,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Eye, Search } from "tabler-icons-react";
import { formatISO } from "date-fns";
import { getDocLockerList } from "../../utilities/apis/agreements";
import { loadingStates, formatDate } from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import AgreementTypeFilter from "../../components/AgreementTypeFilter";
import PartyNameFilter, {
  PARTY_NAME_LIST_TYPES,
} from "../../components/PartyNameFilter";
import UserAvatarView from "../../components/UserAvatarView";
import { agreementStatusColors } from "../../utilities/enums";
import { INDIA_STATES_UNIONS } from "./AgreementForm2";

const statusOptions = [
  { label: "CREATED", value: "CREATED" },
  { label: "REVIEW", value: "REVIEW" },
  { label: "APPROVAL", value: "APPROVAL" },
  { label: "SENT", value: "SENT" },
  { label: "RE-FINALIZED", value: "RE-SIGNATURE" },
  { label: "PARTIALLY COMPLETED", value: "PARTIALLYCOMPLETED" },
  { label: "CANCELLED", value: "CANCELLED" },
  { label: "DECLINED", value: "DECLINED" },
  { label: "COMPLETED", value: "COMPLETED" },
];

const estampOptions = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const AgreementListModal = ({ setSelectedAgreement, onCloseAgreement }) => {
  const [listing, setListing] = useState({
    list: null,
    agreementsCount: null,
    loaded: false,
    sortBy: "id",
    sortDirection: "desc",
    searchString: "",
    statusType: "",
    estamp: null,
    agreementType: null,
    location: null,
    startDate: null,
    endDate: null,
    partyName: "",
  });

  const [uiConfigs, setUiConfigs] = useState({
    loading: loadingStates.NO_ACTIVE_REQUEST,
    page: 1,
  });

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const fetchAgreementsList = async ({ page, filters } = {}) => {
    const activeFilters = filters ?? listing;
    let filterOptions = {};

    if (activeFilters.searchString?.trim()) {
      const id = parseInt(activeFilters.searchString.trim(), 10);
      if (Number.isNaN(id)) {
        showNotification({
          title: "Agreement notification",
          message: "Enter a valid agreement ID (e.g. 3104)",
          color: "red",
        });
        return;
      }
      filterOptions.id = id;
    }

    if (activeFilters.startDate && activeFilters.endDate) {
      filterOptions["createdAt"] = {
        gte: formatISO(activeFilters.startDate),
        lte: formatISO(activeFilters.endDate),
      };
    } else if (activeFilters.startDate || activeFilters.endDate) {
      showNotification({
        title: "Agreement notification",
        message: "Please select both start and end date",
        color: "red",
      });
      return;
    }

    if (activeFilters.agreementType) {
      filterOptions["typeOfAgreement"] = {
        equals: activeFilters.agreementType,
      };
    }
    if (activeFilters.statusType) {
      filterOptions["status"] = { equals: activeFilters.statusType };
    }
    if (activeFilters.estamp) {
      filterOptions["agreementType"] = {
        equals: activeFilters.estamp === "Yes" ? "eStamp" : "normal",
      };
    }
    if (activeFilters.location) {
      filterOptions["location"] = activeFilters.location;
    }
    if (activeFilters.partyName?.length > 0) {
      filterOptions["OR"] = [
        {
          secondParty: {
            path: "$.name",
            string_contains: activeFilters.partyName,
          },
        },
        {
          secondParty: {
            path: "$.indi_name",
            string_contains: activeFilters.partyName,
          },
        },
        {
          secondParty: {
            path: "$.brandName",
            string_contains: activeFilters.partyName,
          },
        },
      ];
    }

    setUiConfigs((prev) => ({ ...prev, loading: loadingStates.LOADING }));

    const payload = {
      pageSize: 10,
      page: page ?? uiConfigs.page,
      sortByOptions: {
        [activeFilters.sortBy]: activeFilters.sortDirection,
      },
      filterOptions,
    };

    const response = await apiWrapWithErrorWithData(getDocLockerList(payload));

    if (response?.success && response?.agreements) {
      setListing((prev) => ({
        ...prev,
        list: response.agreements,
        agreementsCount: response.agreementsCount,
        loaded: true,
      }));
    }

    setUiConfigs((prev) => ({
      ...prev,
      loading: loadingStates.NO_ACTIVE_REQUEST,
    }));
  };

  const handleSearch = () => {
    setUiConfigs((prev) => ({ ...prev, page: 1 }));
    fetchAgreementsList({ page: 1 });
  };

  const handlePageChange = (page) => {
    setUiConfigs((prev) => ({ ...prev, page }));
    fetchAgreementsList({ page });
  };

  const clearFilters = () => {
    setListing((prev) => ({
      ...prev,
      searchString: "",
      agreementType: null,
      statusType: "",
      estamp: null,
      location: null,
      startDate: null,
      endDate: null,
      partyName: "",
    }));
    setUiConfigs((prev) => ({ ...prev, page: 1 }));
    fetchAgreementsList({
      page: 1,
      filters: {
        searchString: "",
        agreementType: null,
        statusType: "",
        estamp: null,
        location: null,
        startDate: null,
        endDate: null,
        partyName: "",
        sortBy: listing.sortBy,
        sortDirection: listing.sortDirection,
      },
    });
  };

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchAgreementsList({ page: 1 });
  }, []);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleCheckboxChange = (id) => {
    setSelectedAgreement(id);
    onCloseAgreement();
  };

  const data = listing?.list;
  const filteredRows =
    data?.filter((v) => v.agreementData?.json?.typeOfAgreement) ?? [];

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      <div className="grid grid-cols-4 gap-3">
        <TextInput
          value={listing.searchString}
          onChange={(e) =>
            setListing((prev) => ({
              ...prev,
              searchString: e.target.value.replace(/[^\d]/g, ""),
            }))
          }
          placeholder="Search by agreement ID"
          icon={<Search size={14} />}
          className="min-w-[200px] w-full"
        />
        <Select
          placeholder="Status"
          value={listing.statusType}
          onChange={(val) =>
            setListing((prev) => ({ ...prev, statusType: val }))
          }
          data={statusOptions}
          clearable
          className="min-w-[180px] w-full"
        />
        <Select
          placeholder="eStamp"
          value={listing.estamp}
          onChange={(val) => setListing((prev) => ({ ...prev, estamp: val }))}
          data={estampOptions}
          clearable
          className="min-w-[140px] w-full"
        />
        <AgreementTypeFilter
          value={listing.agreementType}
          onChange={(val) =>
            setListing((prev) => ({ ...prev, agreementType: val }))
          }
          className="min-w-[180px] w-full"
        />
        <Select
          placeholder="Location"
          searchable
          value={listing.location}
          onChange={(val) => setListing((prev) => ({ ...prev, location: val }))}
          data={INDIA_STATES_UNIONS}
          clearable
          className="min-w-[180px] w-full"
        />
        <DatePicker
          value={listing.startDate}
          onChange={(val) =>
            setListing((prev) => ({ ...prev, startDate: val }))
          }
          placeholder="Created from"
          className="min-w-[160px] w-full"
        />
        <DatePicker
          value={listing.endDate}
          onChange={(val) => setListing((prev) => ({ ...prev, endDate: val }))}
          placeholder="Created to"
          minDate={listing.startDate}
          disabled={!listing.startDate}
          className="min-w-[160px] w-full"
        />
        <PartyNameFilter
          value={listing.partyName}
          onChange={(val) =>
            setListing((prev) => ({ ...prev, partyName: val }))
          }
          listType={PARTY_NAME_LIST_TYPES.DOC_LOCKER}
          className="min-w-[180px] w-full"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          className="bg-[#46BDE1] hover:bg-[#3aa8cc]"
          onClick={handleSearch}
        >
          Search
        </Button>
        <Button variant="outline" color="red" onClick={clearFilters}>
          Clear
        </Button>
      </div>

      {uiConfigs.loading === loadingStates.LOADING && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} height={40} radius="md" />
          ))}
        </div>
      )}

      {listing.loaded &&
        uiConfigs.loading !== loadingStates.LOADING &&
        filteredRows.length > 0 && (
          <div className="w-full min-w-0 rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto [scrollbar-width:thin]">
              <table className="min-w-[1100px] w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 whitespace-nowrap">
                      Select
                    </th>
                    <th className="px-4 py-3 whitespace-nowrap">AR.No.</th>
                    <th className="px-4 py-3 whitespace-nowrap">Type</th>
                    <th className="px-4 py-3 whitespace-nowrap">
                      Business unit
                    </th>
                    <th className="px-4 py-3 whitespace-nowrap">Created by</th>
                    <th className="px-4 py-3 whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 whitespace-nowrap">
                      Second party
                    </th>
                    <th className="px-4 py-3 whitespace-nowrap">Date</th>
                    <th className="sticky right-0 z-10 bg-slate-50 px-4 py-3 whitespace-nowrap text-center">
                      View
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRows.map((row, index) => {
                    const rowBg = index % 2 === 1 ? "bg-slate-50" : "bg-white";

                    return (
                      <tr
                        key={row.id}
                        className={`group transition-colors ${rowBg}`}
                      >
                        <td className={`sticky left-0 z-10 px-4 py-3 ${rowBg}`}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-[#46BDE1] focus:ring-[#46BDE1]"
                            onChange={() => handleCheckboxChange(row.id)}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-500">
                          AGR-{row.id}
                        </td>
                        <td className="px-4 py-3 max-w-[180px] truncate whitespace-nowrap text-slate-800">
                          {row.agreementData.json.typeOfAgreement}
                        </td>
                        <td className="px-4 py-3 max-w-[140px] truncate whitespace-nowrap text-slate-700">
                          {row.agreementData?.json?.location ?? "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <UserAvatarView {...row.createdBy} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge
                            color={
                              agreementStatusColors[row.status] || "orange"
                            }
                          >
                            {row.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 max-w-[180px] truncate whitespace-nowrap text-slate-700">
                          {row.agreementData?.json?.secondParty?.name || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                          {formatDate(row.createdAt)}
                        </td>
                        <td
                          className={`sticky right-0 z-10 px-4 py-3 text-center ${rowBg}`}
                        >
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            to={`/app/agreements_manager/agreements/details/${row.id}`}
                            title="View agreement"
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-[#46BDE1] transition-colors hover:bg-sky-100 hover:text-[#3aa8cc]"
                          >
                            <Eye size={20} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {listing.loaded &&
        uiConfigs.loading !== loadingStates.LOADING &&
        filteredRows.length === 0 && (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
            No agreements found
          </div>
        )}

      {listing.agreementsCount > 0 && (
        <div className="flex justify-center py-2">
          <Pagination
            page={uiConfigs.page}
            onChange={handlePageChange}
            total={Math.ceil(listing.agreementsCount / 10)}
          />
        </div>
      )}
    </div>
  );
};

export default AgreementListModal;
